import {
  AzureGroupInfoResponse,
  LessonGroupMappingType,
  LessonNameAndType,
} from '@mnspro-cloud/services/lib/mnspro-cloud/api/v2';
import { orderBy, unionWith } from 'lodash';
import {
  applyPaginatorOnArray,
  getQuantityOfPages,
} from '../../Pagination/Paginator.utils';
import {
  ErrorType,
  getSequencePositionOfErrorType,
  getSequencePositionOfMappingState,
  MappingState,
} from '../CourseMapping.enums';
import {
  CourseMapping,
  CourseMappingDisplayModel,
  CourseMappingUpdateModel,
  ImportIssue,
  MappingsFilter,
} from '../CourseMapping.interfaces';
import { selectChangedMappings, verifyMappings } from '../CourseMapping.utils';
import { FilteredPaginatedResult } from './CourseMapping.reducer.interfaces';
import { CourseMappingState, DEFAULT_FILTER } from './CourseMapping.state';

export const produceVisibleItems = (
  page: number,
  itemsPerPage: number,
  filteredItems: CourseMappingDisplayModel[] | null,
): CourseMappingDisplayModel[] => {
  let visibleItems = [...(filteredItems ?? [])];
  if (filteredItems) {
    visibleItems = applyPaginatorOnArray(filteredItems, page, itemsPerPage);
  }
  return visibleItems;
};

export const assembleMappingsIncludingUnmapped = (
  initialMappings: CourseMapping[],
  timetableCourses: LessonNameAndType[],
) => {
  return unionWith(
    assembleUnmappedCourses(initialMappings, timetableCourses),
    initialMappings,
    (a, b) =>
      a.timetableCourse.entityRevisionsId === b.timetableCourse.entityRevisionsId,
  );
};

export const assembleUnmappedCourses = (
  initialMappings: CourseMapping[],
  timetableCourses: LessonNameAndType[],
): CourseMapping[] => {
  const filteredUnmappedCourses = timetableCourses.filter(
    a =>
      !initialMappings.some(
        b => b.timetableCourse.entityRevisionsId === a.entityRevisionsId,
      ),
  );
  return filteredUnmappedCourses.map(timetableCourse => {
    //returns array with CourseDisplayModels with unassigned only
    return {
      mappingState: MappingState.Unassigned,
      mappingType: LessonGroupMappingType.course,
      azureGroupId: null,
      azureGroupName: null,
      timetableCourse,
    } as CourseMappingDisplayModel;
  });
};

export const filterMappings = (
  mappingsFilter: MappingsFilter,
  mappingsPool: CourseMappingDisplayModel[],
) => {
  const { searchText, filter } = mappingsFilter;
  const upperSearchText = searchText.toUpperCase();
  return mappingsPool.filter(
    mapping =>
      (mapping.azureGroupName?.toUpperCase().includes(upperSearchText) ||
        mapping.suggestedAzureGroupName
          ?.toUpperCase()
          .includes(upperSearchText) ||
        mapping.timetableCourse?.lessonName
          ?.toUpperCase()
          .includes(upperSearchText)) &&
      filter.length !== 0 &&
      filter.includes(mapping.mappingState),
  );
};

export const setTimetableCoursesInState = (
  state: CourseMappingState,
  timetableCourses: LessonNameAndType[],
): CourseMappingState => {
  let maxPage: number | undefined;
  let courseMappings = null;
  let page: number | undefined;
  let visibleItems: CourseMappingDisplayModel[] | null = null;
  if (state.initialMappings) {
    courseMappings = orderMappings(
      assembleMappingsIncludingUnmapped(state.initialMappings, timetableCourses),
    );

    const itemsPerPage = state.itemsPerPage;
    page = 1;
    maxPage = getQuantityOfPages(courseMappings.length, itemsPerPage);
    visibleItems = applyPaginatorOnArray(courseMappings, page, itemsPerPage);
  }
  return {
    ...state,
    timetableCourses,
    courseMappings,
    maxPage: maxPage ?? state.maxPage,
    visibleItems,
    page: page ?? state.page,
    mappingsFilter: DEFAULT_FILTER,
    hasChanges: false,
  };
};

export const setCloudCoursesInState = (
  state: CourseMappingState,
  cloudCourses: AzureGroupInfoResponse[],
): CourseMappingState => {
  return { ...state, cloudCourses };
};

export const setMappingsInState = (
  state: CourseMappingState,
  courseMappings: CourseMappingDisplayModel[],
): CourseMappingState => {
  if (!state.courseMappings) {
    state.courseMappings = state.initialMappings;
  }
  if (state.courseMappings && state.timetableCourses && state.cloudCourses) {
    const { verifiedMappings } = verifyMappings(
      courseMappings,
      state.cloudCourses,
      state.allowMultipleAzureGroupUsage,
    );

    const sortedAndFilteredMappings = filterMappings(
      state.mappingsFilter,
      orderMappings(verifiedMappings),
    );

    return {
      ...state,
      courseMappings: verifiedMappings,
      visibleItems: applyPaginatorOnArray(
        sortedAndFilteredMappings,
        state.page,
        state.itemsPerPage,
      ),
      maxPage: getQuantityOfPages(
        sortedAndFilteredMappings.length,
        state.itemsPerPage,
      ),
      hasChanges: evaluateChanges(
        verifiedMappings,
        state.timetableCourses,
        state.initialMappings,
      ),
    };
  } else {
    return { ...state };
  }
};

export const updateSingleMappingInState = (
  state: CourseMappingState,
  courseToUpdate: CourseMapping,
): CourseMappingState => {
  // Match on the entity type as well: `entityRevisionsId` alone is not a key for a
  // row. A class and its grade carry the same display name (both come from the
  // Untis `ExternalId`), and if their ids ever coincide, matching on the id alone
  // silently rewrites the wrong row - the edit is lost without any error, which is
  // indistinguishable from "the UI swallowed my change" (Bug #11517).
  const indexOfElementToReplace = (state.courseMappings ?? []).findIndex(
    mapping =>
      mapping.timetableCourse.entityRevisionsId ===
        courseToUpdate.timetableCourse.entityRevisionsId
      && mapping.timetableCourse.groupType === courseToUpdate.timetableCourse.groupType,
  );

  // `updatedMappings[-1] = x` would write a "-1" property and drop the update on
  // the floor. Nothing can be updated that is not in the pool, so keep the state.
  if (indexOfElementToReplace < 0) {
    return state;
  }

  let updatedMappings = [...(state.courseMappings as CourseMapping[])];
  updatedMappings[indexOfElementToReplace] = courseToUpdate;

  const { verifiedMappings } = verifyMappings(
    updatedMappings,
    state.cloudCourses ?? [],
    state.allowMultipleAzureGroupUsage,
  );

  updatedMappings = verifiedMappings;
  const { visibleItems, hasChanges } = filterAndPaginate(
    updatedMappings,
    state.mappingsFilter,
    state.page,
    state.itemsPerPage,
    state.initialMappings,
    state.timetableCourses,
  );

  return {
    ...state,
    courseMappings: updatedMappings,
    visibleItems,
    hasChanges,
    maxPage: getQuantityOfPages(updatedMappings.length, state.itemsPerPage),
  };
};

export const setInitialMappingsInState = (
  state: CourseMappingState,
  initialMappings: CourseMapping[],
): CourseMappingState => {
  let courseMappings = null;
  let page = null;
  let maxPage = null;
  let visibleItems = null;
  let hasChanges = false;
  if (state.timetableCourses && state.cloudCourses) {
    courseMappings = getInitialMappingsWithUnmappedFromState(
      initialMappings,
      state.timetableCourses,
    );
    page = 1;
    const itemsPerPage = state.itemsPerPage;
    const { verifiedMappings } = verifyMappings(
      courseMappings,
      state.cloudCourses,
      state.allowMultipleAzureGroupUsage,
    );
    courseMappings = orderMappings(verifiedMappings);
    // Suggestions are not applied on load, so the working pool equals the
    // baseline and there is nothing to save until the user confirms a row.
    const downstreamResult = filterAndPaginate(
      courseMappings,
      state.mappingsFilter,
      page,
      itemsPerPage,
      initialMappings,
      state.timetableCourses,
    );

    maxPage = getQuantityOfPages(courseMappings.length, itemsPerPage);
    visibleItems = downstreamResult.visibleItems;
    hasChanges = downstreamResult.hasChanges;
  }
  return {
    ...state,
    initialMappings,
    courseMappings,
    maxPage: maxPage ?? state.maxPage,
    visibleItems,
    page: page ?? state.page,
    mappingsFilter: DEFAULT_FILTER,
    hasChanges,
  };
};

export const setMappingsFilterInState = (
  state: CourseMappingState,
  mappingsFilter: MappingsFilter,
): CourseMappingState => {
  const page = 1;
  const itemsPerPage = state.itemsPerPage;
  const filteredItems = filterMappings(
    mappingsFilter,
    state.courseMappings ?? [],
  );
  const maxPage = getQuantityOfPages(filteredItems.length, itemsPerPage);
  const visibleItems = applyPaginatorOnArray(filteredItems, page, itemsPerPage);
  return {
    ...state,
    mappingsFilter,
    page,
    maxPage,
    visibleItems,
  };
};

export const resetMappingsInState = (
  state: CourseMappingState,
): CourseMappingState => {
  const newPage = 1;
  const itemsPerPage = state.itemsPerPage;
  let resettedMappings = null;
  let visibleItems = null;
  let hasChanges = false;
  const cloudCourses = state.cloudCourses;
  const timetableCourses = state.timetableCourses;

  if (state.initialMappings && timetableCourses && cloudCourses) {
    const initialMappingsWithUnmapped = getInitialMappingsWithUnmappedFromState(
      state.initialMappings,
      timetableCourses,
    );
    // Order the pool once and store the ordered array, mirroring
    // setInitialMappingsInState. Storing the raw (verify-only) union here while
    // displaying the ordered list would desync courseMappings from
    // visibleItems: the next updateSingleMapping / setPage paginates
    // courseMappings without re-ordering, so a just-confirmed row would jump to
    // another page (Bug #11208).
    resettedMappings = orderMappings(
      verifyMappings(
        initialMappingsWithUnmapped,
        cloudCourses,
        state.allowMultipleAzureGroupUsage,
      ).verifiedMappings,
    );
    const downstreamResult = filterAndPaginate(
      resettedMappings,
      state.mappingsFilter,
      newPage,
      itemsPerPage,
      state.initialMappings,
      state.timetableCourses,
    );
    visibleItems = downstreamResult.visibleItems;
    hasChanges = downstreamResult.hasChanges;
  }
  const maxPage = getQuantityOfPages(
    resettedMappings?.length ?? 0,
    itemsPerPage,
  );
  return {
    ...state,
    page: newPage,
    visibleItems,
    courseMappings: resettedMappings,
    hasChanges,
    maxPage,
    importIssues: [],
  };
};

export const setPageInState = (
  state: CourseMappingState,
  page: number,
): CourseMappingState => {
  return {
    ...state,
    page,
    visibleItems: produceVisibleItems(
      page,
      state.itemsPerPage,
      filterMappings(state.mappingsFilter, state.courseMappings ?? []),
    ),
  };
};

export const setItemsPerPageInState = (
  state: CourseMappingState,
  itemsPerPage: number,
): CourseMappingState => {
  const newPage = 1;
  const filteredMappingsToPaginate = filterMappings(
    state.mappingsFilter,
    state.courseMappings ?? [],
  );
  const maxPage = getQuantityOfPages(
    filteredMappingsToPaginate.length,
    itemsPerPage,
  );
  return {
    ...state,
    page: newPage,
    itemsPerPage,
    visibleItems: produceVisibleItems(
      newPage,
      itemsPerPage,
      filteredMappingsToPaginate,
    ),
    maxPage,
  };
};

export const setHasChangesInState = (
  state: CourseMappingState,
  hasChanges: boolean,
): CourseMappingState => {
  return { ...state, hasChanges };
};

const filterAndPaginate = (
  mappings: CourseMappingDisplayModel[],
  mappingsFilter: MappingsFilter,
  page: number,
  itemsPerPage: number,
  initialMappings: CourseMappingDisplayModel[] | null,
  timetableCourses: LessonNameAndType[] | null,
): FilteredPaginatedResult<CourseMappingDisplayModel> => {
  const filteredForPaginations = filterMappings(mappingsFilter, mappings);
  return {
    visibleItems: produceVisibleItems(
      page,
      itemsPerPage,
      filteredForPaginations,
    ),
    hasChanges: evaluateChanges(mappings, timetableCourses, initialMappings),
  };
};

const getInitialMappingsWithUnmappedFromState = (
  initialMappings: CourseMappingDisplayModel[] | null,
  timetableCourses: LessonNameAndType[] | null,
): CourseMapping[] =>
  initialMappings && timetableCourses
    ? assembleMappingsIncludingUnmapped(initialMappings, timetableCourses)
    : [];

/**
 * Whether there is anything to save. Deliberately the exact same question
 * `selectChangedMappings` answers on save, keyed by `entityRevisionsId`: a row
 * counts as changed when its applied `azureGroupId` differs from the baseline.
 *
 * The previous implementation walked both arrays **by index** and also compared
 * `error`. Both are wrong: the working pool is `orderMappings`-sorted while the
 * baseline keeps union order, so the rows being compared were not the same rows,
 * and the baseline is never verified, so any error flag counted as a change. The
 * result was an enabled save button with nothing to send - and after the
 * duplicate check was added, flagging a row would itself have looked like a
 * change.
 */
const evaluateChanges = (
  mappingsToCheck: CourseMappingDisplayModel[],
  timetableCourses: LessonNameAndType[] | null,
  initialMappings: CourseMappingDisplayModel[] | null,
): boolean => {
  if (!initialMappings || !timetableCourses) {
    return true;
  }

  const baseline = getInitialMappingsWithUnmappedFromState(
    initialMappings,
    timetableCourses,
  );

  return selectChangedMappings(mappingsToCheck, baseline).length > 0;
};

export const orderMappings = (mappings: CourseMappingDisplayModel[]) =>
  orderBy(
    mappings,
    mapping => [
      getSequencePositionOfErrorType(mapping.error),
      getSequencePositionOfMappingState(mapping.mappingState),
      mapping.timetableCourse,
    ],
    ['asc', 'desc', 'asc'],
  );

export const importMappingsInState = (
  state: CourseMappingState,
  updates: CourseMappingUpdateModel[],
): CourseMappingState => {
  const current = [...(state.courseMappings ?? [])];

  updates.forEach((update) => {
    const index = current.findIndex(
      (mapping) =>
        mapping.timetableCourse.entityRevisionsId
          === update.timetableCourse.entityRevisionsId
        && mapping.timetableCourse.groupType === update.timetableCourse.groupType,
    );
    if (index < 0) {
      return;
    }
    current[index] = {
      ...current[index],
      azureGroupId: update.azureGroupId,
      azureGroupName: update.azureGroupName,
      mappingState: update.azureGroupId
        ? MappingState.ManuallyMapped
        : MappingState.Unassigned,
    };
  });

  return setMappingsInState(state, current);
};

export const setAllowMultipleAzureGroupUsageInState = (
  state: CourseMappingState,
  allowMultipleAzureGroupUsage: boolean,
): CourseMappingState => {
  if (state.allowMultipleAzureGroupUsage === allowMultipleAzureGroupUsage) {
    return state;
  }
  // Re-verify with the new rule so an already-loaded pool picks up (or drops)
  // duplicate flags instead of keeping a verdict from the previous setting.
  return setMappingsInState(
    { ...state, allowMultipleAzureGroupUsage },
    state.courseMappings ?? [],
  );
};

export const setImportIssuesInState = (
  state: CourseMappingState,
  issues: ImportIssue[],
): CourseMappingState => {
  return { ...state, importIssues: issues };
};
